import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Input,
  Grid,
  IconButton,
  FormHelperText,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import { API_ENDPOINTS } from "../../../config/api";

const EditProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_URL = API_ENDPOINTS.PRODUCTS;

  const [product, setProduct] = useState({
    product_name: "",
    description: "",
    brand_id: "",
    category_id: "",
    region_id: "",
    product_photo: null,
    price: "",
    publish: false,
    sort: 1,
  });
  const [currentProductPhotoUrl, setCurrentProductPhotoUrl] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productResponse = await axios.get(API_URL.EDIT(id));
        const productData = productResponse.data.product || {};
        const [brandsResponse, regionsResponse, categoriesResponse] = await Promise.all([
          axios.get(API_ENDPOINTS.BRAND_NAMES),
          axios.get(API_ENDPOINTS.REGION_NAMES),
          axios.get(API_ENDPOINTS.CATEGORY_NAMES),
        ]);

        setProduct({
          product_name: productData.product_name || "",
          description: productData.description || "",
          brand_id: productData.brand_id?.toString() || "",
          category_id: productData.category_id?.toString() || "",
          region_id: productData.region_id?.toString() || "",
          product_photo: null,
          price: productData.price ? productData.price.toString() : "",
          publish: !!productData.publish,
          sort: productData.sort || 1,
        });
        setCurrentProductPhotoUrl(productData.product_photo || null);
        setPreview(productData.product_photo || null);
        setBrands(Array.isArray(brandsResponse.data.brands) ? brandsResponse.data.brands : brandsResponse.data || []);
        setRegions(Array.isArray(regionsResponse.data.regions) ? regionsResponse.data.regions : regionsResponse.data || []);
        setCategories(Array.isArray(categoriesResponse.data.categories) ? categoriesResponse.data.categories : categoriesResponse.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: `Failed to load data: ${error.message}`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [id]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        setProduct((prev) => ({ ...prev, product_photo: null }));
        setPreview(currentProductPhotoUrl || null);
        setFileInputKey(Date.now());
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        setProduct((prev) => ({ ...prev, product_photo: null }));
        setPreview(currentProductPhotoUrl || null);
        setFileInputKey(Date.now());
        return;
      }
      setError("");
      setProduct((prev) => ({ ...prev, product_photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : name === "price" ? (value === "" ? "" : Number(value)) : value,
    }));
  };

  const handlePublishToggle = () => {
    setProduct((prev) => ({ ...prev, publish: !prev.publish }));
  };

  const handleRemoveFile = () => {
    setProduct((prev) => ({ ...prev, product_photo: null }));
    setPreview(currentProductPhotoUrl || null);
    setError("");
    setFileInputKey(Date.now());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!product.product_name.trim()) {
      setSnackbar({ open: true, message: "Product name is required", severity: "error" });
      return;
    }
    if (!product.brand_id) {
      setSnackbar({ open: true, message: "Brand is required", severity: "error" });
      return;
    }
    const priceValue = parseFloat(product.price);
    if (product.price !== "" && product.price !== null && (isNaN(priceValue) || priceValue < 0)) {
      setSnackbar({ open: true, message: "Price must be a non-negative number", severity: "error" });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("product_name", product.product_name);
      formData.append("description", product.description || "");
      formData.append("brand_id", product.brand_id);
      formData.append("category_id", product.category_id);
      formData.append("region_id", product.region_id);
      formData.append("price", product.price === "" ? "" : priceValue.toString());
      formData.append("publish", product.publish ? "1" : "0");
      formData.append("sort", product.sort.toString());
      if (product.product_photo instanceof File) {
        formData.append("product_photo", product.product_photo);
      }
      formData.append("_method", "PUT");

      await axios.post(API_URL.EDIT(id), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbar({ open: true, message: "Product updated successfully!", severity: "success" });
      setTimeout(() => navigate("/products-list"), 1500);
    } catch (error) {
      console.error("Error updating product:", error);
      let userMessage = "Failed to update product.";
      if (error.response?.data?.errors) {
        userMessage = Object.values(error.response.data.errors).flat().join(", ");
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      }
      setSnackbar({ open: true, message: userMessage, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false, message: "", severity: "success" }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 5, flexDirection: "column", gap: 2 }}>
        <CircularProgress />
        <Typography>Loading product data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Card sx={{ p: 4, width: "100%", boxShadow: 3 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <div className="image-field">
                  <Input
                    id="product_photo"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    key={fileInputKey}
                    sx={{ display: 'none' }}
                  />
                  <label htmlFor="product_photo">
                    <Box
                      sx={{
                        border: '2px dashed #B0C4DE',
                        borderRadius: '10px',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#F0F8FF',
                        cursor: 'pointer',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      {preview ? (
                        <>
                          <img
                            src={product.product_photo instanceof File ? preview : currentProductPhotoUrl}
                            alt="Uploaded"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '80px',
                              marginBottom: '10px',
                              objectFit: 'contain',
                            }}
                          />
                          <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                            {product.product_photo instanceof File ? product.product_photo.name : "Current Image"}
                          </Typography>
                          <IconButton
                            onClick={(e) => {
                              e.preventDefault();
                              handleRemoveFile();
                            }}
                            sx={{
                              position: 'absolute',
                              top: '5px',
                              right: '5px',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <UploadIcon
                            sx={{ fontSize: '40px', color: '#4682B4', marginBottom: '10px' }}
                          />
                          <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                            Upload Product Photo
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#4682B4' }}>
                            Supports: JPG, JPEG, PNG
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </div>
                {error && <FormHelperText error>{error}</FormHelperText>}
                <FormHelperText className="text-center">Upload product image (PNG, JPG, JPEG, max 5MB)</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <TextField
                  label="Product Name"
                  name="product_name"
                  value={product.product_name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="Price"
                  name="price"
                  value={product.price}
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Brand</InputLabel>
                <Select
                  name="brand_id"
                  value={product.brand_id}
                  onChange={handleChange}
                  label="Brand"
                  required
                >
                  <MenuItem value="">Select Brand</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id.toString()}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category_id"
                  value={product.category_id}
                  onChange={handleChange}
                  label="Category"
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={12}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select
                  name="region_id"
                  value={product.region_id}
                  onChange={handleChange}
                  label="Region"
                >
                  <MenuItem value="">Select Region</MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region.id} value={region.id.toString()}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <TextField
                  label="Description"
                  name="description"
                  value={product.description}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={product.publish}
                    onChange={handlePublishToggle}
                    color="primary"
                  />
                }
                label="Published"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" gap={2} mt={3}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : "Update Product"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => navigate("/products-list")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditProductForm;